import { Document } from 'libs/document/document.model';

export enum displayRole {
  apprentice = 'Stagiaire',
  manager = 'Coordonnateur',
  tutor = 'Tuteur',
  alumni = 'Alumni',
}

export class User {
  public static EMPTY_USER = new User('', '', '', '', '', false, true, [], []);

  constructor(
    public firstName: string,
    public lastName: string,
    public email: string,
    public phone: string,
    public role: string,
    public isAdmin: boolean,
    public isRegistered: boolean,
    public trainings: string[],
    public fcmTokens: string[],
    public trainingCenterId?: string,
    public networkId?: string,
    public id?: string,
    public authId?: string,
    public status?: string,
    public avatar?: string,
    public companyName?: string,
    public companyAddress?: string,
    public results?: any[],
    public evaluations?: any[],
    public documents?: Document[],
    public tutors?: User[],
    public apprentices?: User[],
    public evaluationsByQuestionnaireId?: { [questionnaireId: string]: any },
    public evaluationsByTrainingId?: { [trainingId: string]: any },
  ) {}

  static fromObject(data: any): User {
    // Add id to documents
    const documents = data?.documents
      ? data?.documents.map((doc: Document, index: number) => {
          doc.id = index.toString();
          return doc;
        })
      : [];
    const user = new User(
      data?.firstName,
      data?.lastName,
      data?.email,
      data?.phone?.replace(/\s+/g, ''),
      data?.role,
      data?.isAdmin ? data?.isAdmin : false,
      data?.isRegistered ? data?.isRegistered : false,
      data?.trainings,
      data?.fcmTokens ? data?.fcmTokens : [],
      data?.trainingCenterId,
      data?.networkId,
      data?.id,
      data?.authId,
      data?.status,
      data?.avatar,
      data?.companyName,
      data?.companyAddress,
      data?.results ? data?.results : [],
      data?.evaluations ? data?.evaluations : [],
      documents,
    );

    // Toujours initialises, meme sans evaluation : les gabarits indexent ces
    // objets sans garde — `apprentice?.evaluationsByTrainingId[trainingId]`
    // protege `apprentice`, pas l'index. Laisser le champ indefini fait lever
    // « Cannot read properties of undefined » a l'affichage du menu stagiaire
    // pour toute fiche sans evaluation. Un objet vide se comporte partout
    // comme « aucune evaluation ».
    user.evaluationsByQuestionnaireId = {};
    user.evaluationsByTrainingId = {};

    if (data?.evaluations) {
      user.evaluationsByQuestionnaireId = data?.evaluations.reduce((evaluationsByQuestionnaireId, evaluation) => {
        evaluationsByQuestionnaireId[evaluation.questionnaireId] = evaluation;
        return evaluationsByQuestionnaireId;
      }, {});

      user.evaluationsByTrainingId = data?.evaluations.reduce((evaluationsByTrainingId, evaluation) => {
        if (!evaluationsByTrainingId[evaluation.trainingId]) {
          evaluationsByTrainingId[evaluation.trainingId] = [];
        }
        evaluationsByTrainingId[evaluation.trainingId].push(evaluation);
        return evaluationsByTrainingId;
      }, {});
    }

    return user;
  }

  static toObject(user: User): any {
    const data = {
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      phone: user.phone.toString().replace(/\s+/g, ''),
      role: user.role,
      isAdmin: user.isAdmin ? true : false,
      isRegistered: user.isRegistered ? true : false,
      trainings: user.trainings,
      fcmTokens: user.fcmTokens ?? [],
      avatar: user.avatar ?? '',
    };

    if (user.role === 'apprentice') {
      data['status'] = user.status ?? 'Stagiaire';
    }

    if (user.role === 'tutor') {
      // Valeur de repli obligatoire : Firestore refuse `undefined` et fait
      // echouer l'ecriture entiere. Un tuteur dont la fiche n'a pas ces champs
      // ne pouvait plus se deconnecter — logout() met a jour la fiche pour
      // retirer le jeton FCM (user.service.ts:419), et l'updateDoc levait
      // « Unsupported field value: undefined ». Meme traitement que `avatar`
      // et `fcmTokens` ci-dessus.
      data['companyName'] = user.companyName ?? '';
      data['companyAddress'] = user.companyAddress ?? '';
    }

    // S4-04 : `results`/`evaluations` ne transitent plus jamais par une
    // sauvegarde generale du compte (createOrUpdateUser/updateUser). Un ecran
    // reste facilement ouvert avec une copie de ces tableaux - une sauvegarde
    // non liee (changer un telephone, par ex.) les reecrirait en entier avec
    // cette copie, ressuscitant silencieusement une reponse supprimee
    // ailleurs entre-temps (S4-03). Les seules ecritures day-to-day passent
    // par arrayUnion/arrayRemove (UserService.addQuestionnaireResponse /
    // deleteQuestionnaireResponse) ; les rares mutations deliberees et
    // scopees de ces tableaux (archivage a la reinscription, nettoyage a la
    // suppression d'une formation) passent par
    // UserService.setArchivedQuestionnaireResponses, qui ecrit directement
    // ces deux champs sans repasser par toObject.

    if (user.authId) {
      data['authId'] = user.authId;
    }

    if (user.trainingCenterId) {
      data['trainingCenterId'] = user.trainingCenterId;
    }

    if (user.networkId) {
      data['networkId'] = user.networkId;
    }

    if (user.documents) {
      data['documents'] = user.documents;
    }

    return data;
  }

  get displayRole() {
    return displayRole[this.role];
  }

  hasTrainingEvalution({ trainingId }: { trainingId: string }) {
    return (
      this.evaluations.filter(
        (evaluation) =>
          evaluation.trainingId === trainingId && evaluation.tutorId !== 'manager' && !evaluation.managerId, // Exclude manager evaluations (old format with managerId)
      ).length > 0
    );
  }

  getLastTrainingEvaluationLevel({ trainingId }: { trainingId: string }): number | undefined {
    const trainingEvaluations = this.evaluations.filter(
      (evaluation) => evaluation.trainingId === trainingId && evaluation.tutorId !== 'manager' && !evaluation.managerId, // Exclude manager evaluations (old format with managerId)
    );
    return trainingEvaluations.length > 0 ? trainingEvaluations[trainingEvaluations.length - 1].level : undefined;
  }

  static getDisplayFields(role: string) {
    const displayFields: any[] = [
      { name: 'lastName', header: 'Nom', size: 2 },
      { name: 'firstName', header: 'Prénom', size: 2 },
      { name: 'email', header: 'E-mail', size: 4 },
      { name: 'phone', header: 'Tel.', size: 2 },
    ];

    if (role === 'apprentice') {
      displayFields.push({ name: 'status', header: 'Statut', size: 3 });
    }

    if (role === 'tutor') {
      displayFields.push({ name: 'companyName', header: 'Structure', size: 3 });
    }

    return displayFields;
  }

  static getImportFields(role: string) {
    let importFields: any[] = ['lastName', 'firstName', 'email', 'phone'];

    if (role === 'apprentice') {
      importFields = [
        ...importFields,
        'status',
        'tutorLastName',
        'tutorFirstName',
        'tutorEmail',
        'tutorPhone',
        'tutorCompanyName',
        'tutorCompanyAddress',
      ];
    }

    return importFields;
  }
}
